import { Component, Injector } from '@angular/core';
import { FormatsService } from '@fgpp-ui/components';
import dayjs from 'dayjs';
import { SERVER_TIME_FORMAT } from '../../../core/consts/general.const';
import { IEndpoint } from '../../../core/interfaces/endpoint.interface';
import { IValueChange } from '../../../core/interfaces/form-value-change.interface';
import { BFOnFormLoadComplete, BFOnValueChange } from '../../../core/interfaces/sdk-profile-hooks.interface';
import { ProfileFormTemplateProviders } from '../../../core/templates/profile-form.template.providers';
import { AbstractProfileFormComponent } from '../../abstract/abstract-profile/abstract-profile-form.component';
import { ProfileActions } from '../../enums/profile-actions.enum';
import { ProfileLayoutType } from '../../enums/profile-layout-type.enum';
import { ServiceLevelAgreement } from '../../models/service-level-agreement.model';
import { EarliestProcessingTime } from './enums/earliest-processing-time.enum';
import { ProcessTiming } from './enums/process-timing.enum';

@Component({
  selector: 'app-service-level-agreement',
  templateUrl: '../../../core/templates/profile-form.template.html',
  styleUrls: ['../../../core/templates/profile-form.template.scss'],
  providers: ProfileFormTemplateProviders
})
export class ServiceLevelAgreementComponent extends AbstractProfileFormComponent implements BFOnFormLoadComplete, BFOnValueChange {

  constructor(protected injector: Injector,
              private formatsService: FormatsService) {
    super(injector);
  }

  fgppFormLoadComplete(): void {
    const isViewMode = this.layoutMode === ProfileLayoutType.VIEW;
    this.updateProcessTimingControls(false, isViewMode);
    this.updateClientFormatForTimeControls();
    this.updateTimeZoneControls();
    this.updateEarliestProcessingTimeControls(false, isViewMode);
    this.updateNotifyControls(false, isViewMode);
  }

  fgppValueChanges(change: IValueChange): void {
    switch (change.control) {
      case ServiceLevelAgreement.PROCESS_IMMEDIATLY:
        this.updateProcessTimingControls();
        break;
      case ServiceLevelAgreement.PROCESS_AFTER_TOD:
        this.updateAfterTimeZoneControl();
        break;
      case ServiceLevelAgreement.PROCESS_BEFORE_TOD:
        this.updateBeforeTimeZoneControl();
        break;
      case ServiceLevelAgreement.EARLIEST_PROC:
        this.updateEarliestProcessingTimeControls();
        break;
      case ServiceLevelAgreement.NOTIFY_MI_TO_PAY_TIME_CHK:
        this.updateNotifyPayByTimeControl();
        break;
      case ServiceLevelAgreement.NOTIFY_MI_TO_CUTOFF_CHK:
        this.updateNotifyCutoffControl();
        break;
    }
  }

  protected getServerActionRequestBody(endpoint: IEndpoint, buttonId: string): any {
    const body = super.getServerActionRequestBody(endpoint, buttonId);
    const action = ProfileActions[buttonId];
    if (action === ProfileActions.CREATE || action === ProfileActions.SAVE || action === ProfileActions.APPROVE) {
      this.updateServerFormatForTimeControls(body);
    }
    return body;
  }

  private updateProcessTimingControls(changed = true, isViewMode = false): void {
    const processTimingControl = this.formMgr.get(ServiceLevelAgreement.PROCESS_IMMEDIATLY);
    const minutesBeforePayByTimeControl = this.formMgr.get(ServiceLevelAgreement.HOLD_MI_TO_MSG_PAY_TIME);
    const minutesFromReceiptControl = this.formMgr.get(ServiceLevelAgreement.HOLD_MI_FROM_RECEIPT);
    switch (processTimingControl.getValue()) {
      case ProcessTiming.ProcessImmediately:
        minutesBeforePayByTimeControl.disable();
        minutesFromReceiptControl.disable();
        if (changed) {
          minutesBeforePayByTimeControl.markAsOptional().markAsUntouched().setValue('');
          minutesFromReceiptControl.markAsOptional().markAsUntouched().setValue('');
        }
        break;
      case ProcessTiming.HoldBeforePayByTime:
        minutesBeforePayByTimeControl.markAsRequired();
        minutesFromReceiptControl.disable();
        if (!isViewMode) {
          minutesBeforePayByTimeControl.enable();
        }
        if (changed) {
          minutesFromReceiptControl.markAsOptional().markAsUntouched().setValue('');
        }
        break;
      case ProcessTiming.HoldFromReceipt:
        minutesBeforePayByTimeControl.disable();
        minutesFromReceiptControl.markAsRequired();
        if (changed) {
          minutesBeforePayByTimeControl.markAsOptional().markAsUntouched().setValue('');
        }
        if (!isViewMode) {
          minutesFromReceiptControl.enable();
        }
        break;
    }
  }

  private updateClientFormatForTimeControls(): void {
    this.updateClientFormatForTimeControl(ServiceLevelAgreement.PROCESS_AFTER_TOD);
    this.updateClientFormatForTimeControl(ServiceLevelAgreement.PROCESS_BEFORE_TOD);

  }

  private updateServerFormatForTimeControls(body: any): void {
    this.updateServerFormatForTimeControl(body, ServiceLevelAgreement.PROCESS_BEFORE_TOD);
    this.updateServerFormatForTimeControl(body, ServiceLevelAgreement.PROCESS_AFTER_TOD);
  }

  private updateClientFormatForTimeControl(controlId: ServiceLevelAgreement): void {
    if (!controlId) {
      return;
    }
    const value = this.formMgr.get(controlId).getValue();
    if (value && dayjs(value).isValid()) {
      const time = dayjs(value).format(this.userPreferences.timeFormat);
      this.formMgr.get(controlId).setValue(time);
    }
  }

  private updateServerFormatForTimeControl(body: any, controlId: ServiceLevelAgreement): void {
    if (body[controlId]) {
      body[controlId] = this.formatsService.toTime(body[controlId]).format(SERVER_TIME_FORMAT);
    }
  }

  private updateTimeZoneControls(): void {
    this.updateAfterTimeZoneControl();
    this.updateBeforeTimeZoneControl();
  }

  private updateAfterTimeZoneControl(): void {
    this.updateTimeZoneControl(ServiceLevelAgreement.PROCESS_AFTER_TOD, ServiceLevelAgreement.PROCESS_AFTER_TOD_TZ);
  }

  private updateBeforeTimeZoneControl(): void {
    this.updateTimeZoneControl(ServiceLevelAgreement.PROCESS_BEFORE_TOD, ServiceLevelAgreement.PROCESS_BEFORE_TOD_TZ);
  }

  private updateTimeZoneControl(timeControlId: ServiceLevelAgreement, timeZoneControlId: ServiceLevelAgreement): void {
    const timeControl = this.formMgr.get(timeControlId);
    const timeZoneControl = this.formMgr.get(timeZoneControlId);
    if (timeControl.getValue()) {
      timeZoneControl.markAsRequired();
    } else {
      timeZoneControl.markAsOptional();
    }
  }

  private updateEarliestProcessingTimeControls(changed = true, isViewMode = false): void {
    if (isViewMode) {
      return;
    }
    const earliestProcessingTimeConrol = this.formMgr.get(ServiceLevelAgreement.EARLIEST_PROC);
    const minutesFromReceiptControl = this.formMgr.get(ServiceLevelAgreement.MIN_MINUTES_FROM_RECEIPT);
    const minutesAfterAcceptanceControl = this.formMgr.get(ServiceLevelAgreement.MINUTES_FROM_ACCEPTANCE);
    switch (earliestProcessingTimeConrol.getValue()) {
      case EarliestProcessingTime.FromReceipt:
        minutesFromReceiptControl.enable();
        minutesAfterAcceptanceControl.disable();
        if (changed) {
          minutesAfterAcceptanceControl.markAsUntouched().setValue('');
        }
        break;
      case EarliestProcessingTime.AfterAcceptance:
        minutesFromReceiptControl.disable();
        minutesAfterAcceptanceControl.enable();
        if (changed) {
          minutesFromReceiptControl.markAsUntouched().setValue('');
        }
        break;
      default:
        minutesFromReceiptControl.disable();
        minutesAfterAcceptanceControl.disable();
        break;
    }
  }

  private updateNotifyControls(changed = true, isViewMode = false): void {
    this.updateNotifyPayByTimeControl(changed, isViewMode);
    this.updateNotifyCutoffControl(changed, isViewMode);
  }

  private updateNotifyPayByTimeControl(changed = true, isViewMode = false): void {
    this.updateNotifyControl(ServiceLevelAgreement.NOTIFY_MI_TO_PAY_TIME_CHK, ServiceLevelAgreement.NOTIFY_MI_TO_PAY_TIME, changed, isViewMode);
  }

  private updateNotifyCutoffControl(changed = true, isViewMode = false): void {
    this.updateNotifyControl(ServiceLevelAgreement.NOTIFY_MI_TO_CUTOFF_CHK, ServiceLevelAgreement.NOTIFY_MI_TO_CUTOFF, changed, isViewMode);
  }

  private updateNotifyControl(notifyCheckboxControlId: ServiceLevelAgreement, notifyMinutesControlId: ServiceLevelAgreement,
    changed = true, isViewMode = false): void {
    const notifyCheckboxControl = this.formMgr.get(notifyCheckboxControlId);
    const notifyMinutesControl = this.formMgr.get(notifyMinutesControlId);
    if (notifyCheckboxControl.getValue() === '1') {
      notifyMinutesControl.markAsRequired();
      if (!isViewMode) {
        notifyMinutesControl.enable();
      }
    } else {
      notifyMinutesControl.disable().markAsOptional();
      if (changed) {
        notifyMinutesControl.markAsUntouched().setValue('');
      }
    }
  }
}
